import { useState, useEffect, useRef } from 'react'
import { Box, VStack, HStack, Text, Badge, Alert, Button, Input } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { User, AlertTriangle, Calendar } from 'lucide-react'

// GCD helper function
function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

// Simplify fraction
function simplifyFraction(numerator: number, denominator: number): [number, number] {
  const divisor = gcd(numerator, denominator)
  return [numerator / divisor, denominator / divisor]
}

interface OwnerShare {
  ownerId: string
  ownerName: string
  ownershipId?: string // Existing ownership ID if updating
  shareNumerator: number // Fraction numerator
  shareDenominator: number // Fraction denominator
  isNew?: boolean // True for the owner being added
}

interface OwnershipDistributorProps {
  unitNumber: string
  existingOwners: Array<{
    ownerId: string
    ownerName: string
    ownershipId: string
    shareNumerator: number
    shareDenominator: number
  }>
  newOwnerName: string
  startDate: string
  onStartDateChange: (date: string) => void
  onDistributionChange: (shares: OwnerShare[]) => void
}

export function OwnershipDistributor({
  unitNumber,
  existingOwners,
  newOwnerName,
  startDate,
  onStartDateChange,
  onDistributionChange,
}: OwnershipDistributorProps) {
  // Initialize shares: existing owners keep their shares, new owner gets 0
  const [shares, setShares] = useState<OwnerShare[]>(() => {
    const existing = existingOwners.map((owner) => ({
      ownerId: owner.ownerId,
      ownerName: owner.ownerName,
      ownershipId: owner.ownershipId,
      shareNumerator: owner.shareNumerator,
      shareDenominator: owner.shareDenominator,
      isNew: false,
    }))

    const newOwner: OwnerShare = {
      ownerId: 'new',
      ownerName: newOwnerName,
      shareNumerator: 0,
      shareDenominator: 1,
      isNew: true,
    }

    return [...existing, newOwner]
  })

  // Store previous unit number to detect real changes
  const prevUnitNumberRef = useRef(unitNumber)

  // Reset shares when unit changes (detected by unitNumber change only)
  useEffect(() => {
    // Only reset if unitNumber actually changed (not on every render)
    if (prevUnitNumberRef.current === unitNumber) {
      return
    }

    prevUnitNumberRef.current = unitNumber

    const existing = existingOwners.map((owner) => ({
      ownerId: owner.ownerId,
      ownerName: owner.ownerName,
      ownershipId: owner.ownershipId,
      shareNumerator: owner.shareNumerator,
      shareDenominator: owner.shareDenominator,
      isNew: false,
    }))

    const newOwner: OwnerShare = {
      ownerId: 'new',
      ownerName: newOwnerName,
      shareNumerator: 0,
      shareDenominator: 1,
      isNew: true,
    }

    setShares([...existing, newOwner])
    // Reset the initial mount flag when unit changes
    isInitialMount.current = true
  }, [unitNumber, existingOwners, newOwnerName])

  // Calculate total as a fraction
  const calculateTotal = (): [number, number] => {
    if (shares.length === 0) return [0, 1]

    // Find common denominator (LCM of all denominators)
    const denominators = shares.map(s => s.shareDenominator)
    const lcm = denominators.reduce((a, b) => (a * b) / gcd(a, b))

    // Sum all numerators after converting to common denominator
    const totalNumerator = shares.reduce((sum, owner) => {
      return sum + (owner.shareNumerator * lcm) / owner.shareDenominator
    }, 0)

    return simplifyFraction(totalNumerator, lcm)
  }

  const [totalNum, totalDen] = calculateTotal()
  const isValid = totalNum === totalDen // Valid when fraction equals 1 (100%)

  // Use ref to track if this is the initial mount
  const isInitialMount = useRef(true)

  // Notify parent of changes (skip initial mount to avoid loop)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    onDistributionChange(shares)
  }, [shares])

  const autoDistribute = () => {
    // Distribute equally among all owners using fractions
    const denominator = shares.length
    const updatedShares = shares.map((owner) => ({
      ...owner,
      shareNumerator: 1,
      shareDenominator: denominator,
    }))
    setShares(updatedShares)
  }

  const updateShareNumerator = (index: number, numerator: number) => {
    const updatedShares = [...shares]
    updatedShares[index]!.shareNumerator = numerator
    setShares(updatedShares)
  }

  const updateShareDenominator = (_: number, denominator: number) => {
    // When denominator changes, update ALL shares to use the same denominator
    const updatedShares = shares.map((share) => ({
      ...share,
      shareDenominator: denominator,
    }))
    setShares(updatedShares)
  }

  return (
    <Box>
      <VStack align="stretch" gap={4}>
        {/* Warning alert */}
        <Alert.Root status="warning" size="sm">
          <HStack gap={2} align="start">
            <AlertTriangle size={16} />
            <Box>
              <Alert.Title>Lot déjà affecté</Alert.Title>
              <Alert.Description>
                Le lot {unitNumber} est déjà affecté à{' '}
                {existingOwners.length === 1
                  ? 'un autre copropriétaire'
                  : `${existingOwners.length} autres copropriétaires`}
                . Vous devez répartir les parts entre tous les copropriétaires.
              </Alert.Description>
            </Box>
          </HStack>
        </Alert.Root>

        {/* Start date field */}
        <Field.Root required>
          <Field.Label fontSize="sm" fontWeight="semibold">
            <HStack gap={2}>
              <Calendar size={14} />
              <Text>Date de début</Text>
            </HStack>
          </Field.Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            size="sm"
          />
        </Field.Root>

        {/* Distribution controls */}
        <Box>
          <HStack justify="space-between" mb={3}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              Répartition des parts du lot {unitNumber}
            </Text>
            <Button
              type="button"
              size="xs"
              variant="outline"
              colorPalette="brand"
              onClick={autoDistribute}
            >
              Répartir équitablement
            </Button>
          </HStack>

          <VStack align="stretch" gap={3}>
            {shares.map((owner, index) => (
              <Box
                key={owner.ownerId}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor={owner.isNew ? 'brand.300' : 'gray.200'}
                bg={owner.isNew ? 'brand.50' : 'white'}
              >
                <HStack gap={3} align="center">
                  <HStack gap={2} flex={1}>
                    <User size={14} />
                    <Text fontSize="sm" fontWeight="medium">
                      {owner.ownerName}
                    </Text>
                    {owner.isNew && (
                      <Badge colorPalette="brand" size="xs" variant="solid">
                        Nouveau
                      </Badge>
                    )}
                  </HStack>

                  <HStack gap={2} align="center">
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      Quote-part:
                    </Text>
                    <HStack gap={1} align="center">
                      <Input
                        type="number"
                        min={0}
                        value={owner.shareNumerator}
                        onChange={(e) => updateShareNumerator(index, Number(e.target.value) || 0)}
                        size="xs"
                        width="60px"
                        textAlign="center"
                      />
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">/</Text>
                      <Input
                        type="number"
                        min={1}
                        value={owner.shareDenominator}
                        onChange={(e) => updateShareDenominator(index, Number(e.target.value) || 1)}
                        size="xs"
                        width="60px"
                        textAlign="center"
                      />
                    </HStack>
                  </HStack>
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Validation message */}
        {!isValid && (
          <Alert.Root status="error" size="sm">
            <Alert.Description>
              La somme des parts doit être égale à 1. Actuellement: {totalNum}/{totalDen}
            </Alert.Description>
          </Alert.Root>
        )}
      </VStack>
    </Box>
  )
}
